import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import {
    fetchMeadiaSourceData,
    fetchAllSubMediaSource,
    fetchUserData,
    getPreSalesSitevisitReport,
} from "../Services/APIService";

/* ===================== INTERFACES ===================== */

interface LeadAssignmentLogDto {
    logId: number;
    leadId: number;
    assignedUser: string;
    createdByUser: string;
    followUpType: string;
    remark: string;
    leadStatus: string;
    notes: string;
    projectName: string;
    source: string;
    subSource: string;
    isNextActionRequired: boolean;
    createdOn: string;
}

interface NextFollowupLead {
    leadId: number;
    leadName: string;
    phoneNumber: string;
    countryCode: string;
    email: string;
    leadStatus: string;
    source: string;
    subSource: string;
    leadCreatedOn: string;
    assignedUser: string;
    lastFollowUpStatus: string;
    followUpType: string;
    remark: string;
    followUpDate: string;
    projectName: string;
    firstAssignedUser: string;
    currentAssignedUser: string;
    assignmentHistory: LeadAssignmentLogDto[];
}

interface FilterParams {
    tenantId: number;
    fromDate?: string;
    toDate?: string;
    sourceId?: number;
    subSourceId?: number;
    firstAssignedUserId?: number;
    currentAssignedUserId?: number;
}

interface MediaSource {
    sourceId: number;
    sourceName: string;
    isActive: boolean;
}

interface SubMediaSource {
    mediaSourceID: number;
    name: string;
    id: number;
    isActive: boolean;
}

interface User {
    userId: number;
    userName: string;
    userTypeId: number;
    isActive: boolean;
    reportTo: number;
}

interface OptionType {
    value: number;
    label: string;
}

/* ===================== COMPONENT ===================== */

const PreSalesSitevisit: React.FC = (): React.ReactElement => {
    const navigate = useNavigate();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [leads, setLeads] = useState<NextFollowupLead[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);

    const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
    const [selectedLeadHistory, setSelectedLeadHistory] = useState<{
        leadName: string;
        firstAssignedUser: string;
        currentAssignedUser: string;
        logs: LeadAssignmentLogDto[];
    } | null>(null);

    const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
    const [subMediaSources, setSubMediaSources] = useState<SubMediaSource[]>([]);
    const [filteredSubSources, setFilteredSubSources] = useState<SubMediaSource[]>([]);
    const [users, setUsers] = useState<User[]>([]);

    const tenantId = parseInt(localStorage.getItem("tenantId") || "1000");
    const [selectedPeriod, setSelectedPeriod] = useState<string>("");

    const [filterParams, setFilterParams] = useState<FilterParams>({
        tenantId,
        fromDate: "",
        toDate: "",
        sourceId: undefined,
        subSourceId: undefined,
        firstAssignedUserId: undefined,
        currentAssignedUserId: undefined,
    });

    /* ===================== INIT ===================== */

    useEffect(() => {
        fetchNextFollowupData();
        fetchDropdownData();
    }, []);

    /* ===================== DATA ===================== */

    const fetchNextFollowupData = async (): Promise<void> => {
        try {
            setLoading(true);
            const res: any = await getPreSalesSitevisitReport({ TenantId: tenantId });
            setLeads(res?.userNextFollowUp || []);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load next follow-up data");
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async (): Promise<void> => {
        try {
            const sourceResponse = await fetchMeadiaSourceData();
            setMediaSources(sourceResponse.filter((s: MediaSource) => s.isActive));

            const subSourceResponse = await fetchAllSubMediaSource();
            setSubMediaSources(subSourceResponse.filter((s: SubMediaSource) => s.isActive));

            const userResponse = await fetchUserData();
            setUsers(
                userResponse.filter(
                    (u: User) => (u.userTypeId === 2 || u.userTypeId === 4) && u.isActive
                )
            );
        } catch (e) {
            console.error("Error fetching dropdown data:", e);
            toast.error("Failed to load filter options");
        }
    };

    /* ===================== FILTER ===================== */

    const applyFilter = async (): Promise<void> => {
        try {
            setLoading(true);
            const params: any = { TenantId: tenantId };
            if (filterParams.fromDate) params.fromDate = filterParams.fromDate;
            if (filterParams.toDate) params.toDate = filterParams.toDate;
            if (filterParams.sourceId) params.sourceId = filterParams.sourceId;
            if (filterParams.subSourceId) params.subSourceId = filterParams.subSourceId;
            if (filterParams.firstAssignedUserId) params.firstAssignedUserId = filterParams.firstAssignedUserId;
            if (filterParams.currentAssignedUserId) params.currentAssignedUserId = filterParams.currentAssignedUserId;

            const res: any = await getPreSalesSitevisitReport(params);
            setLeads(res?.userNextFollowUp || []);
            toast.success("Filter applied successfully");
        } catch (e) {
            console.error("Error applying filter:", e);
            toast.error("Failed to apply filter");
        } finally {
            setLoading(false);
        }
    };

    const clearFilter = (): void => {
        setFilterParams({
            tenantId,
            fromDate: "",
            toDate: "",
            sourceId: undefined,
            subSourceId: undefined,
            firstAssignedUserId: undefined,
            currentAssignedUserId: undefined,
        });
        setSelectedPeriod("");
        setFilteredSubSources([]);
        fetchNextFollowupData();
    };

    /* ===================== SEARCH ===================== */

    const filteredLeads = leads.filter((l: NextFollowupLead) => {
        const t = searchText.toLowerCase().trim();
        if (!t) return true;
        return (
            l.leadName.toLowerCase().includes(t) ||
            l.assignedUser.toLowerCase().includes(t) ||
            l.phoneNumber.includes(t)
        );
    });

    /* ===================== HISTORY MODAL ===================== */

    const openHistoryModal = (lead: NextFollowupLead): void => {
        setSelectedLeadHistory({
            leadName: lead.leadName,
            firstAssignedUser: lead.firstAssignedUser || "N/A",
            currentAssignedUser: lead.currentAssignedUser || "N/A",
            logs: lead.assignmentHistory || [],
        });
        setHistoryModalOpen(true);
    };

    const closeHistoryModal = (): void => {
        setHistoryModalOpen(false);
        setSelectedLeadHistory(null);
    };

    /* ===================== EXPORT ===================== */

    const exportToExcel = (): void => {
        try {
            const headers = [
                "Sr No",
                "Lead Id",
                "Lead Name",
                "Mobile Number",
                "Lead Date",
                "Site Visited Date",
                "Site Visited By",
                "Project",
                // "Follow-up Type",
                "Remark",
                "Assigned",
                "Last Follow-up Date",
                "Last Updated By",
                "Follow-up Type",
                "Current Stage",
                "Stage Update Date",
            ];

            const rows = filteredLeads.map((lead: NextFollowupLead, index: number) => {
                // ── Last log entry (most recent activity) ──────────────────
                const sortedLogs = [...(lead.assignmentHistory || [])].sort(
                    (a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()
                );
                const lastLog = sortedLogs[0] ?? null;

                // ── Helpers ────────────────────────────────────────────────
                const formatDate = (dateStr?: string | null): string => {
                    if (!dateStr) return "-";
                    const d = new Date(dateStr);
                    if (isNaN(d.getTime())) return "-";
                    const dd = String(d.getDate()).padStart(2, "0");
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    const yyyy = d.getFullYear();
                    return `${dd}/${mm}/${yyyy}`;
                };

                const formatDateTime = (dateStr?: string | null): string => {
                    if (!dateStr) return "-";
                    const d = new Date(dateStr);
                    if (isNaN(d.getTime())) return "-";
                    const dd = String(d.getDate()).padStart(2, "0");
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    const yyyy = d.getFullYear();
                    let hrs = d.getHours();
                    const mins = String(d.getMinutes()).padStart(2, "0");
                    const ampm = hrs >= 12 ? "PM" : "AM";
                    hrs = hrs % 12 || 12;
                    return `${dd}/${mm}/${yyyy} ${String(hrs).padStart(2, "0")}:${mins} ${ampm}`;
                };

                return [
                    index + 1,                                                          // Sr No
                    lead.leadId,                                                        // Lead Id
                    lead.leadName || "N/A",                                             // Lead Name
                    `${lead.countryCode || ""} ${lead.phoneNumber || ""}`.trim() || "N/A", // Mobile Number
                    formatDate(lead.leadCreatedOn),                                     // Lead Date
                    formatDateTime(lead.followUpDate),                                  // Site Visited Date
                    lead.firstAssignedUser || "N/A",                                   // Site Visited By
                    lead.projectName || "-",                                            // Project
                    // lastLog?.followUpType || "-",                                        // Follow-up Type
                    lead.remark || "-",                                              // Remark
                    lead.currentAssignedUser || "N/A",                                 // Assigned
                    formatDateTime(lastLog?.createdOn),                                 // Last Follow-up Date
                    lastLog?.createdByUser || "-",                                      // Last Updated By
                    lead.followUpType || "N/A",                                       // Follow-up Type (last log)
                    lead.leadStatus || "-",                                             // Stage
                    formatDate(lastLog?.createdOn),                                     // Stage Update Date
                ];
            });

            // ── Build CSV ──────────────────────────────────────────────────
            const escape = (cell: any): string =>
                `"${String(cell ?? "").replace(/"/g, '""')}"`;

            let csvContent = headers.map(escape).join(",") + "\n";
            rows.forEach((row) => {
                csvContent += row.map(escape).join(",") + "\n";
            });

            // ── Trigger download ───────────────────────────────────────────
            const blob = new Blob(["\uFEFF" + csvContent], {
                type: "text/csv;charset=utf-8;",
            });
            const link = document.createElement("a");
            link.setAttribute("href", URL.createObjectURL(blob));
            link.setAttribute(
                "download",
                `pre-sales-sitevisit-${new Date().toISOString().split("T")[0]}.csv`
            );
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success("Excel file exported successfully");
        } catch (error) {
            console.error("Error exporting to Excel:", error);
            toast.error("Failed to export Excel file");
        }
    };

    const exportToPDF = (): void => {
        try {
            const bodyRows = filteredLeads
                .map(
                    (lead: NextFollowupLead) => `
          <tr>
            <td>${lead.leadName}</td>
            <td>${lead.countryCode || ""}${lead.phoneNumber}</td>
            <td>${lead.firstAssignedUser || "N/A"}</td>
            <td>${lead.currentAssignedUser || "N/A"}</td>
            <td>${lead.followUpType}</td>
            <td>${new Date(lead.followUpDate).toLocaleString()}</td>
            <td>${lead.projectName || "-"}</td>
          </tr>`
                )
                .join("");

            const printContent = `
        <!DOCTYPE html><html><head>
        <title>Pre Sales Site Visit Report</title>
        <style>
          @page { size: A4 landscape; margin: 1cm; }
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #333; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #a78590; color: white; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .status-cell { text-align: center; font-weight: bold; }
          .meta-info { margin-bottom: 15px; font-size: 12px; color: #666; }
        </style>
        </head><body>
        <h1>Pre Sales Site Visit Report</h1>
        <div class="meta-info">
          <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
          ${filterParams.fromDate ? `<strong>From:</strong> ${filterParams.fromDate}<br/>` : ""}
          ${filterParams.toDate ? `<strong>To:</strong> ${filterParams.toDate}<br/>` : ""}
          <strong>Total Records:</strong> ${filteredLeads.length}
        </div>
        <table>
          <thead><tr>
            <th>Lead Name</th><th>Phone</th>
            <th>First Assigned</th><th>Current Assigned</th>
            <th>Follow-up Type</th><th>Follow-up Date</th>
            <th>Project</th>
          </tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
        </body></html>`;

            const printWindow = window.open("", "_blank");
            if (printWindow) {
                printWindow.document.write(printContent);
                printWindow.document.close();
                printWindow.onload = () => {
                    printWindow.print();
                    toast.success("PDF print dialog opened");
                };
            } else {
                toast.error("Please allow pop-ups to export PDF");
            }
        } catch (error) {
            console.error("Error exporting to PDF:", error);
            toast.error("Failed to export PDF");
        }
    };

    /* ===================== SOURCE CHANGE ===================== */

    const handleSourceOnChange = (sourceId: number | undefined): void => {
        setFilterParams({ ...filterParams, sourceId, subSourceId: undefined });
    };

    useEffect(() => {
        if (filterParams.sourceId && subMediaSources.length > 0) {
            const filtered = subMediaSources.filter(
                (src: SubMediaSource) => src.mediaSourceID === filterParams.sourceId
            );
            setFilteredSubSources(filtered);
            if (!filtered.some((s: SubMediaSource) => s.id === filterParams.subSourceId)) {
                setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
            }
        } else {
            setFilteredSubSources([]);
            setFilterParams((prev) => ({ ...prev, subSourceId: undefined }));
        }
    }, [filterParams.sourceId, subMediaSources]);

    /* ===================== USER OPTIONS ===================== */

    const userTypeId = Number(localStorage.getItem("userTypeId"));
    const loggedUserId = Number(localStorage.getItem("UserId"));
    const isLoginAsManager = localStorage.getItem("isLoginAsManager") === "true";

    let filteredUserList = users;
    if (userTypeId === 1) {
        filteredUserList = users;
    } else if (userTypeId === 2) {
        if (isLoginAsManager) {
            filteredUserList = users.filter(
                (u) => u.userId === loggedUserId || u.reportTo === loggedUserId
            );
        } else {
            filteredUserList = users.filter((u) => u.userId === loggedUserId);
        }
    } else {
        filteredUserList = users.filter((u) => u.userId === loggedUserId);
    }

    const userOptions: OptionType[] = filteredUserList.map((u: User) => ({
        value: u.userId,
        label: u.userName,
    }));

    /* ===================== HELPERS ===================== */

    const leadDashboardRedirection = (leadId: number): void => {
        navigate("/LeadDashboard", { state: { leadId } });
    };

    const formatFollowUpDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return `${day}/${month}/${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    };

    /* ===================== COLUMNS ===================== */

    const columns = [
        {
            name: "Lead Name",
            selector: (row: NextFollowupLead) => row.leadName,
            sortable: true,
            width: "220px",
            cell: (row: NextFollowupLead) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div
                        style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "rgb(167, 133, 144)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "bold",
                            flexShrink: 0,
                        }}
                    >
                        {row.leadName?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <a
                        href="#"
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            leadDashboardRedirection(row.leadId);
                        }}
                        style={{
                            textDecoration: "none",
                            fontWeight: 500,
                            color: "#33475b",
                        }}
                    >
                        {row.leadName || "N/A"}
                    </a>
                </div>
            ),
        },
        // {
        //     name: "Phone",
        //     cell: (row: NextFollowupLead) => (
        //         <span>
        //             {`${row.countryCode || ""} ${row.phoneNumber || ""}`.trim() || "N/A"}
        //         </span>
        //     ),
        //     sortable: true,
        //     width: "150px",
        // },
        {
            name: "Site Visited By",
            selector: (row: NextFollowupLead) => row.firstAssignedUser || "N/A",
            sortable: true,
            width: "160px",
            cell: (row: NextFollowupLead) => (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <i
                        className="bi bi-person-check"
                        style={{ color: "#6c757d", fontSize: "14px" }}
                    />
                    <span style={{ fontSize: "13px" }}>
                        {row.firstAssignedUser || "N/A"}
                    </span>
                </div>
            ),
        },
        {
            name: "Current Assigned",
            selector: (row: NextFollowupLead) => row.currentAssignedUser || "N/A",
            sortable: true,
            width: "165px",
            cell: (row: NextFollowupLead) => (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <i
                        className="bi bi-person-fill"
                        style={{ color: "#a78590", fontSize: "14px" }}
                    />
                    <span style={{ fontSize: "13px", fontWeight: 500 }}>
                        {row.currentAssignedUser || "N/A"}
                    </span>
                </div>
            ),
        },
        {
            name: "Stage",
            selector: (row: NextFollowupLead) => row.leadStatus,
            sortable: true,
            width: "150px",
        },
        {
            name: "Site Visited Date",
            selector: (row: NextFollowupLead) => row.followUpDate,
            sortable: true,
            width: "200px",
            cell: (row: NextFollowupLead) => (
                <span>{formatFollowUpDate(row.followUpDate)}</span>
            ),
        },
        {
            name: "Project",
            selector: (row: NextFollowupLead) => row.projectName || "-",
            sortable: true,
            width: "180px",
        },
    ];

    /* ===================== OPTIONS ===================== */

    const getSelectedOption = (
        options: OptionType[],
        value?: number
    ): OptionType | null =>
        options.find((o: OptionType) => o.value === value) || null;

    const sourceOptions: OptionType[] = mediaSources.map((s: MediaSource) => ({
        value: s.sourceId,
        label: s.sourceName,
    }));

    const subSourceOptions: OptionType[] = filteredSubSources.map(
        (s: SubMediaSource) => ({
            value: s.id,
            label: s.name,
        })
    );

    const toggleSidebar = (): void =>
        setIsSidebarCollapsed(!isSidebarCollapsed);

    /* ===================== UI ===================== */

    return (
        <div className="wrapper">
            <Toaster
                position="top-center"
                reverseOrder={false}
                containerStyle={{ zIndex: 10000 }}
            />
            {!isSidebarCollapsed && <CollapsibleSidebar />}
            <div className="main">
                <Navbar onToggleSidebar={toggleSidebar} />
                <div className="main-content-div">
                    <div className="main-header-div"></div>
                    <main className="content mainContent">
                        <div className="container-fluid p-0">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header carHeaderWithBtn">
                                            <div className="card-title mb-0">
                                                Pre Sales Site Visit Report
                                            </div>
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <input
                                                    type="text"
                                                    className="p-2 datatableSearchInput"
                                                    placeholder="Search Lead / User / Phone"
                                                    value={searchText}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        setSearchText(e.target.value)
                                                    }
                                                    style={{ width: "250px" }}
                                                />
                                                <button
                                                    className="demo-btn btn btn-outline-success btn-sm"
                                                    onClick={exportToPDF}
                                                    title="Export to PDF">
                                                    <i className="bi bi-file-pdf" /> Export PDF
                                                </button>
                                                <button
                                                    className="demo-btn btn btn-outline-success btn-sm"
                                                    onClick={exportToExcel}
                                                    title="Export to Excel">
                                                    <i className="bi bi-file-excel" /> Export Excel
                                                </button>
                                                <button
                                                    className="demo-btn btn btn-outline-primary btn-sm"
                                                    onClick={() =>
                                                        setIsFilterExpanded(!isFilterExpanded)
                                                    }>
                                                    <i
                                                        className={`bi bi-${isFilterExpanded ? "chevron-up" : "funnel"
                                                            }`}
                                                    />{" "}
                                                    {isFilterExpanded ? "Hide Filters" : "Show Filters"}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Filter Section */}
                                        {isFilterExpanded && (
                                            <div
                                                className="card-body"
                                                style={{
                                                    borderBottom: "1px solid #dee2e6",
                                                    backgroundColor: "#f8f9fa",
                                                }}>
                                                <div className="row g-3">
                                                    {/* From Date */}
                                                    <div className="col-md-3">
                                                        <label className="form-label">Site Visit From Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={filterParams.fromDate}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                setFilterParams({
                                                                    ...filterParams,
                                                                    fromDate: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    </div>

                                                    {/* To Date */}
                                                    <div className="col-md-3">
                                                        <label className="form-label">Site Visit To Date</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            value={filterParams.toDate}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                setFilterParams({
                                                                    ...filterParams,
                                                                    toDate: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    </div>

                                                    {/* Source */}
                                                    <div className="col-md-3">
                                                        <label className="form-label">Source</label>
                                                        <Select
                                                            options={sourceOptions}
                                                            value={getSelectedOption(
                                                                sourceOptions,
                                                                filterParams.sourceId
                                                            )}
                                                            onChange={(option: OptionType | null) =>
                                                                handleSourceOnChange(option?.value)
                                                            }
                                                            className="basic-single-select"
                                                            classNamePrefix="select"
                                                            placeholder="Select Source"
                                                            isSearchable
                                                            isClearable
                                                        />
                                                    </div>

                                                    {/* Sub Source */}
                                                    <div className="col-md-3">
                                                        <label className="form-label">Sub Source</label>
                                                        <Select
                                                            options={subSourceOptions}
                                                            value={getSelectedOption(
                                                                subSourceOptions,
                                                                filterParams.subSourceId
                                                            )}
                                                            onChange={(option: OptionType | null) =>
                                                                setFilterParams({
                                                                    ...filterParams,
                                                                    subSourceId: option?.value,
                                                                })
                                                            }
                                                            className="basic-single-select"
                                                            classNamePrefix="select"
                                                            placeholder={
                                                                filterParams.sourceId
                                                                    ? "Select Sub Source"
                                                                    : "Select source first"
                                                            }
                                                            isSearchable
                                                            isClearable
                                                            isDisabled={!filterParams.sourceId}
                                                        />
                                                    </div>

                                                    {/* First Assigned User */}
                                                    <div className="col-md-6">
                                                        <label className="form-label">
                                                            <i
                                                                className="bi bi-person-check me-1"
                                                                style={{ color: "#6c757d" }}
                                                            />
                                                            First Assigned User
                                                        </label>
                                                        <Select
                                                            options={userOptions}
                                                            value={getSelectedOption(
                                                                userOptions,
                                                                filterParams.firstAssignedUserId
                                                            )}
                                                            onChange={(option: OptionType | null) =>
                                                                setFilterParams({
                                                                    ...filterParams,
                                                                    firstAssignedUserId: option?.value,
                                                                })
                                                            }
                                                            className="basic-single-select"
                                                            classNamePrefix="select"
                                                            placeholder="Select First Assigned User"
                                                            isSearchable
                                                            isClearable
                                                        />
                                                    </div>

                                                    {/* Current Assigned User */}
                                                    <div className="col-md-6">
                                                        <label className="form-label">
                                                            <i
                                                                className="bi bi-person-fill me-1"
                                                                style={{ color: "#a78590" }}
                                                            />
                                                            Current Assigned User
                                                        </label>
                                                        <Select
                                                            options={userOptions}
                                                            value={getSelectedOption(
                                                                userOptions,
                                                                filterParams.currentAssignedUserId
                                                            )}
                                                            onChange={(option: OptionType | null) =>
                                                                setFilterParams({
                                                                    ...filterParams,
                                                                    currentAssignedUserId: option?.value,
                                                                })
                                                            }
                                                            className="basic-single-select"
                                                            classNamePrefix="select"
                                                            placeholder="Select Current Assigned User"
                                                            isSearchable
                                                            isClearable
                                                        />
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div
                                                        className="col-12"
                                                        style={{
                                                            display: "flex",
                                                            gap: "10px",
                                                            justifyContent: "flex-end",
                                                        }}>
                                                        <button
                                                            className="btn btn-secondary"
                                                            onClick={clearFilter}>
                                                            Clear
                                                        </button>
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={applyFilter}>
                                                            Apply Filter
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="card-body p-0">
                                            <DataTable
                                                columns={columns}
                                                data={filteredLeads}
                                                pagination
                                                paginationPerPage={10}
                                                highlightOnHover
                                                striped
                                                responsive
                                                noDataComponent="No Pre Sales Site Visit data found"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
                <Footer />
            </div>

            <OverlayLoader
                loading={loading}
                message="Loading Pre Sales Site Visit Report"
                submessage="Please wait..."
            />

            {/* ===================== HISTORY MODAL ===================== */}
            {historyModalOpen && selectedLeadHistory && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                    }}
                    onClick={closeHistoryModal}>
                    <div
                        style={{
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            width: "100%",
                            maxWidth: "860px",
                            maxHeight: "85vh",
                            overflowY: "auto",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                        }}
                        onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                            e.stopPropagation()
                        }>
                        {/* Modal Header */}
                        <div
                            style={{
                                padding: "18px 24px",
                                borderBottom: "1px solid #dee2e6",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "#a78590",
                                borderRadius: "12px 12px 0 0",
                            }}>
                            <div>
                                <h5 style={{ margin: 0, color: "#fff", fontWeight: 700 }}>
                                    <i className="bi bi-clock-history" /> Assignment & Log History
                                </h5>
                                <small style={{ color: "rgba(255,255,255,0.85)" }}>
                                    {selectedLeadHistory.leadName}
                                </small>
                            </div>
                            <button
                                onClick={closeHistoryModal}
                                style={{
                                    background: "rgba(255,255,255,0.2)",
                                    border: "none",
                                    borderRadius: "50%",
                                    width: "32px",
                                    height: "32px",
                                    cursor: "pointer",
                                    color: "#fff",
                                    fontSize: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}>
                                ×
                            </button>
                        </div>

                        {/* Assignment Summary Cards */}
                        <div
                            style={{
                                display: "flex",
                                gap: "16px",
                                padding: "16px 24px",
                                backgroundColor: "#fdf6f7",
                                borderBottom: "1px solid #dee2e6",
                            }}>
                            <div
                                style={{
                                    flex: 1,
                                    backgroundColor: "#fff",
                                    border: "1px solid #e8d5d9",
                                    borderRadius: "8px",
                                    padding: "12px 16px",
                                }}>
                                <div style={{ fontSize: "11px", color: "#6c757d", marginBottom: "4px" }}>
                                    FIRST ASSIGNED TO
                                </div>
                                <div style={{ fontWeight: 600, color: "#33475b", fontSize: "14px" }}>
                                    <i className="bi bi-person-check" style={{ color: "#a78590" }} />{" "}
                                    {selectedLeadHistory.firstAssignedUser}
                                </div>
                            </div>

                            <div
                                style={{
                                    flex: 1,
                                    backgroundColor: "#fff",
                                    border: "1px solid #e8d5d9",
                                    borderRadius: "8px",
                                    padding: "12px 16px",
                                }}>
                                <div style={{ fontSize: "11px", color: "#6c757d", marginBottom: "4px" }}>
                                    CURRENTLY ASSIGNED TO
                                </div>
                                <div style={{ fontWeight: 600, color: "#33475b", fontSize: "14px" }}>
                                    <i className="bi bi-person-fill" style={{ color: "#a78590" }} />{" "}
                                    {selectedLeadHistory.currentAssignedUser}
                                </div>
                            </div>

                            <div
                                style={{
                                    flex: 1,
                                    backgroundColor: "#fff",
                                    border: "1px solid #e8d5d9",
                                    borderRadius: "8px",
                                    padding: "12px 16px",
                                }}>
                                <div style={{ fontSize: "11px", color: "#6c757d", marginBottom: "4px" }}>
                                    TOTAL LOG ENTRIES
                                </div>
                                <div style={{ fontWeight: 600, color: "#33475b", fontSize: "14px" }}>
                                    <i className="bi bi-journal-text" style={{ color: "#a78590" }} />{" "}
                                    {selectedLeadHistory.logs.length}
                                </div>
                            </div>
                        </div>

                        {/* Log Timeline */}
                        <div style={{ padding: "20px 24px" }}>
                            {selectedLeadHistory.logs.length === 0 ? (
                                <div style={{ textAlign: "center", color: "#6c757d", padding: "30px" }}>
                                    <i className="bi bi-journal-x" style={{ fontSize: "32px" }} />
                                    <p style={{ marginTop: "10px" }}>No log entries found for this lead.</p>
                                </div>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                    {selectedLeadHistory.logs.map(
                                        (log: LeadAssignmentLogDto, index: number) => (
                                            <div
                                                key={log.logId}
                                                style={{
                                                    border: "1px solid #e8d5d9",
                                                    borderRadius: "8px",
                                                    padding: "14px 16px",
                                                    backgroundColor: index % 2 === 0 ? "#fff" : "#fdf6f7",
                                                }}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        marginBottom: "8px",
                                                    }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                        <span
                                                            style={{
                                                                backgroundColor: "#a78590",
                                                                color: "#fff",
                                                                borderRadius: "50%",
                                                                width: "22px",
                                                                height: "22px",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "11px",
                                                                fontWeight: 700,
                                                            }}>
                                                            {index + 1}
                                                        </span>
                                                        <strong style={{ color: "#33475b", fontSize: "13px" }}>
                                                            {log.followUpType || "N/A"}
                                                        </strong>
                                                        {log.isNextActionRequired && (
                                                            <span
                                                                style={{
                                                                    backgroundColor: "#fff3cd",
                                                                    color: "#856404",
                                                                    fontSize: "11px",
                                                                    padding: "2px 8px",
                                                                    borderRadius: "10px",
                                                                    fontWeight: 600,
                                                                }}>
                                                                Next Action Required
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span style={{ fontSize: "12px", color: "#6c757d" }}>
                                                        <i className="bi bi-calendar3" />{" "}
                                                        {new Date(log.createdOn).toLocaleString()}
                                                    </span>
                                                </div>

                                                <div
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "1fr 1fr 1fr",
                                                        gap: "8px",
                                                        fontSize: "12px",
                                                        color: "#495057",
                                                        marginBottom: log.notes ? "8px" : 0,
                                                    }}>
                                                    <div>
                                                        <span style={{ color: "#6c757d" }}>Assigned To: </span>
                                                        <strong>{log.assignedUser || "N/A"}</strong>
                                                    </div>
                                                    <div>
                                                        <span style={{ color: "#6c757d" }}>Created By: </span>
                                                        <strong>{log.createdByUser || "N/A"}</strong>
                                                    </div>
                                                    <div>
                                                        <span style={{ color: "#6c757d" }}>Lead Status: </span>
                                                        <strong>{log.leadStatus || "N/A"}</strong>
                                                    </div>
                                                    <div>
                                                        <span style={{ color: "#6c757d" }}>Project: </span>
                                                        <strong>{log.projectName || "N/A"}</strong>
                                                    </div>
                                                    <div>
                                                        <span style={{ color: "#6c757d" }}>Source: </span>
                                                        <strong>{log.source || "N/A"}</strong>
                                                    </div>
                                                    <div>
                                                        <span style={{ color: "#6c757d" }}>Sub Source: </span>
                                                        <strong>{log.subSource || "N/A"}</strong>
                                                    </div>
                                                </div>

                                                {log.notes && (
                                                    <div
                                                        style={{
                                                            backgroundColor: "#f8f9fa",
                                                            border: "1px solid #e9ecef",
                                                            borderRadius: "6px",
                                                            padding: "8px 12px",
                                                            fontSize: "12px",
                                                            color: "#495057",
                                                            marginTop: "6px",
                                                        }}>
                                                        <i className="bi bi-chat-left-text" style={{ marginRight: "6px" }} />
                                                        {log.notes}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        .status-count-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 13px;
          display: inline-block;
          transition: all 0.2s;
        }
        .status-count-badge:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .status-5 { background: #e0f2f1; color: #00695c; }
        .status-6 { background: #ffebee; color: #b71c1c; }
        .css-1nmdiq5-menu {
          position: relative;
          z-index: 10000;
        }
      `}</style>
        </div>
    );
};

export default PreSalesSitevisit;